import React, { useCallback, useEffect, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { IoClose } from "react-icons/io5";
import { DEFAULT_MAX_OTP_SENDS, OTP_ACTION_RESEND, OTP_ACTION_SEND, OTP_PURPOSE_EMAIL_VERIFICATION, OTP_ROLE_CAREGIVER, OTP_ROLE_FAMILY_MEMBER, sendEmailOtp, sendMobileOtp, validateEmailOtp, validateMobileOtp } from "../../common/api/familyportalOtpApi";
import { PROFILE_ROLE_CAREGIVER, PROFILE_ROLE_FAMILY_MEMBER, sendEditMobileOtp, validateEditMobileOtp } from "../../common/api/profileApi";
import {
  clearPersistedEmailVerifyResendLock,
  clearPersistedResendCooldown,
  formatResendLockError,
  getCooldownRemainingSeconds,
  isOtpWaitMessage,
  isSendOtpRateLimitError,
  isSendOtpTooSoonError,
  parseLockedUntilMs,
  readPersistedEmailVerifyResendLock,
  readPersistedResendCooldown,
  resolveEmailVerifyLockEndsAt,
  resolveResendCooldownEndsAt,
  writePersistedEmailVerifyResendLock,
  writePersistedResendCooldown,
} from "../../common/utils/forgotPasswordResendLock";
import { CONTACT_VERIFICATION_ROLE } from "../../common/utils/contactVerification";
import * as Sentry from "@sentry/react";

export const EMAIL_VERIFY_SUCCESS_MESSAGE = "Email verified successfully.";
export const OTP_MODE_VERIFY = "verify";
export const OTP_MODE_EDIT_MOBILE = "editMobile";

const EMAIL_OTP_LENGTH = 6;
const MOBILE_OTP_LENGTH = 6;
const RESEND_LOCK_MS = 15 * 60 * 1000;
const OTP_ERROR_CLEAR_MS = 5000;

const buildEmptyDigits = (len) => Array.from({ length: len }, () => "");

const resolveOtpApiRole = (verificationRole) => (verificationRole === CONTACT_VERIFICATION_ROLE.CAREGIVER ? OTP_ROLE_CAREGIVER : OTP_ROLE_FAMILY_MEMBER);

const resolveProfileApiRole = (verificationRole) => (verificationRole === CONTACT_VERIFICATION_ROLE.CAREGIVER ? PROFILE_ROLE_CAREGIVER : PROFILE_ROLE_FAMILY_MEMBER);

const FamilyOtpVerifyDialog = ({ open, onClose, target, email, mobileNumber, contactDisplay, verificationRole = CONTACT_VERIFICATION_ROLE.FAMILY, onSuccess, onSendRateLimited, initialOtpMeta = null, mode = OTP_MODE_VERIFY }) => {
  const isEmailTarget = target === "email" && Boolean(email);
  const isEditMobileMode = mode === OTP_MODE_EDIT_MOBILE && !isEmailTarget;
  const otpLength = isEmailTarget ? EMAIL_OTP_LENGTH : MOBILE_OTP_LENGTH;
  const otpApiRole = resolveOtpApiRole(verificationRole);
  const profileApiRole = resolveProfileApiRole(verificationRole);

  const [digits, setDigits] = useState(() => buildEmptyDigits(otpLength));
  const [activeIndex, setActiveIndex] = useState(0);
  const [error, setError] = useState("");
  const [sendError, setSendError] = useState("");
  const [resent, setResent] = useState(false);
  const [otpMeta, setOtpMeta] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [resendCooldownEndsAt, setResendCooldownEndsAt] = useState(null);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
  const [resendLockEndsAt, setResendLockEndsAt] = useState(null);
  const [resendLockSeconds, setResendLockSeconds] = useState(0);

  const inputRefs = useRef([]);
  const errorTimerRef = useRef(null);
  const sendInitialOtpRef = useRef(null);
  const lastSubmittedOtpRef = useRef("");
  const openRef = useRef(open);
  openRef.current = open;

  const otp = digits.join("");

  const clearErrorTimer = useCallback(() => {
    if (errorTimerRef.current) {
      window.clearTimeout(errorTimerRef.current);
      errorTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearErrorTimer(), [clearErrorTimer]);
  useEffect(() => {
    if (!resendCooldownEndsAt) {
      setResendCooldownSeconds(0);
      return;
    }
    const tick = () => {
      const rem = Math.max(0, Math.ceil((resendCooldownEndsAt - Date.now()) / 1000));
      setResendCooldownSeconds(rem);
      if (rem === 0) {
        setResendCooldownEndsAt(null);
        clearPersistedResendCooldown();
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [resendCooldownEndsAt]);

  const startResendLock = useCallback((emailAddr, data) => {
    setResendCooldownEndsAt(null);
    setResendCooldownSeconds(0);
    clearPersistedResendCooldown();

    const lockEndsAt = resolveEmailVerifyLockEndsAt({
      email: emailAddr,
      lockedUntil: data?.lockedUntil,
      attemptsRemaining: data?.attemptsRemaining ?? 0,
    });
    if (!lockEndsAt) {
      return;
    }

    setResendLockEndsAt(lockEndsAt);
    writePersistedEmailVerifyResendLock(emailAddr, lockEndsAt);
  }, []);

  useEffect(() => {
    if (!resendLockEndsAt) {
      setResendLockSeconds(0);
      return;
    }
    const tick = () => {
      const rem = Math.max(0, Math.ceil((resendLockEndsAt - Date.now()) / 1000));
      setResendLockSeconds(rem);
      if (rem === 0) {
        clearPersistedEmailVerifyResendLock();
        setResendLockEndsAt(null);
        setOtpMeta(null);
        setSendError("");
        if (openRef.current) {
          sendInitialOtpRef.current?.();
        }
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [resendLockEndsAt]);

  const startResendCooldown = useCallback((data, emailAddr, message = "") => {
    const cooldownEndsAt = resolveResendCooldownEndsAt(data?.nextRequestAfter, message, data?.minRequestIntervalSeconds);
    if (!cooldownEndsAt) {
      return;
    }

    setResendCooldownEndsAt(cooldownEndsAt);
    setResendCooldownSeconds(getCooldownRemainingSeconds(cooldownEndsAt));
    writePersistedResendCooldown(emailAddr, cooldownEndsAt);
  }, []);

  const applyOtpMeta = useCallback(
    (data, emailAddr, message = "") => {
      if (!data) return;
      setOtpMeta(data);
      startResendCooldown(data, emailAddr, message);

      if (data.attemptsRemaining === 0 || data.lockedUntil) {
        const lockEndsAt = resolveEmailVerifyLockEndsAt({
          email: emailAddr,
          lockedUntil: data.lockedUntil,
          attemptsRemaining: data.attemptsRemaining,
        });
        if (lockEndsAt) {
          setResendLockEndsAt(lockEndsAt);
          writePersistedEmailVerifyResendLock(emailAddr, lockEndsAt);
        }
        return;
      }

      clearPersistedEmailVerifyResendLock();
      setResendLockEndsAt(null);
    },
    [startResendCooldown],
  );

  const showOtpError = useCallback(
    (message) => {
      clearErrorTimer();
      setError(message);
      errorTimerRef.current = window.setTimeout(() => {
        setError("");
        errorTimerRef.current = null;
      }, OTP_ERROR_CLEAR_MS);
    },
    [clearErrorTimer],
  );

  const sendInitialOtp = useCallback(async () => {
    if (!isEmailTarget || !email) {
      return;
    }

    const activeLock = readPersistedEmailVerifyResendLock(email);
    if (activeLock && activeLock > Date.now()) {
      setResendLockEndsAt(activeLock);
      setSendError("");
      return;
    }

    setIsSending(true);
    setSendError("");
    try {
      const result = await sendEmailOtp({
        email,
        purpose: OTP_PURPOSE_EMAIL_VERIFICATION,
        action: OTP_ACTION_SEND,
        role: otpApiRole,
      });

      if (!result.success) {
        if (result.data) {
          setOtpMeta(result.data);
        }

        if (isSendOtpRateLimitError(result.data)) {
          const lockEndsAt = resolveEmailVerifyLockEndsAt({
            email,
            lockedUntil: result.data?.lockedUntil,
            attemptsRemaining: result.data?.attemptsRemaining ?? 0,
          });
          startResendLock(email, result.data);
          if (lockEndsAt) {
            onSendRateLimited?.(lockEndsAt);
          }
          onClose();
          return;
        }

        if (isSendOtpTooSoonError(result.data, result.message) || isOtpWaitMessage(result.message)) {
          if (result.data) {
            applyOtpMeta(result.data, email, result.message);
          } else {
            startResendCooldown(null, email, result.message);
          }
          setSendError("");
          setOtpSent(true);
          window.setTimeout(() => inputRefs.current[0]?.focus(), 80);
          return;
        }

        setSendError(result.message || "Failed to send verification code.");
        return;
      }

      applyOtpMeta(result.data, email);
      setOtpSent(true);
      window.setTimeout(() => inputRefs.current[0]?.focus(), 80);
    } catch (error) {
      Sentry.captureException(error);
      setSendError("Failed to send verification code.");
    } finally {
      setIsSending(false);
    }
  }, [applyOtpMeta, email, isEmailTarget, onClose, onSendRateLimited, otpApiRole, startResendCooldown, startResendLock]);

  sendInitialOtpRef.current = sendInitialOtp;

  useEffect(() => {
    if (!open) return;

    const emptyDigits = buildEmptyDigits(isEmailTarget ? EMAIL_OTP_LENGTH : MOBILE_OTP_LENGTH);
    setDigits(emptyDigits);
    setActiveIndex(0);
    setError("");
    setSendError("");
    setResent(false);
    setOtpMeta(null);
    setOtpSent(false);
    setResendCooldownEndsAt(null);
    setResendLockEndsAt(null);
    lastSubmittedOtpRef.current = "";
    clearErrorTimer();

    if (!isEmailTarget) {
      window.setTimeout(() => inputRefs.current[0]?.focus(), 80);
      setOtpSent(true);
      if (initialOtpMeta) {
        setOtpMeta(initialOtpMeta);
        const cooldownEndsAt = resolveResendCooldownEndsAt(initialOtpMeta.nextRequestAfter, "", initialOtpMeta.minRequestIntervalSeconds);
        if (cooldownEndsAt) {
          setResendCooldownEndsAt(cooldownEndsAt);
          setResendCooldownSeconds(getCooldownRemainingSeconds(cooldownEndsAt));
        }
      }
      return;
    }

    if (initialOtpMeta) {
      setOtpMeta(initialOtpMeta);
      setOtpSent(true);
      const cooldownEndsAt = resolveResendCooldownEndsAt(initialOtpMeta.nextRequestAfter, "", initialOtpMeta.minRequestIntervalSeconds);
      if (cooldownEndsAt) {
        setResendCooldownEndsAt(cooldownEndsAt);
        setResendCooldownSeconds(getCooldownRemainingSeconds(cooldownEndsAt));
      }
      window.setTimeout(() => inputRefs.current[0]?.focus(), 80);
      return;
    }

    const persisted = readPersistedEmailVerifyResendLock(email);
    if (persisted && persisted > Date.now()) {
      setResendLockEndsAt(persisted);
      return;
    }

    const persistedCooldown = readPersistedResendCooldown(email);
    if (persistedCooldown) {
      setResendCooldownEndsAt(persistedCooldown);
      setResendCooldownSeconds(getCooldownRemainingSeconds(persistedCooldown));
    }

    sendInitialOtp();
  }, [open, email, target, isEmailTarget, sendInitialOtp, clearErrorTimer, initialOtpMeta]);

  const updateDigit = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    clearErrorTimer();
    setError("");
    if (digit && index < otpLength - 1) {
      inputRefs.current[index + 1]?.focus();
      setActiveIndex(index + 1);
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace") {
      if (digits[index]) {
        const next = [...digits];
        next[index] = "";
        setDigits(next);
        clearErrorTimer();
        setError("");
        return;
      }
      if (index > 0) {
        inputRefs.current[index - 1]?.focus();
        setActiveIndex(index - 1);
      }
    }
    if (event.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
      setActiveIndex(index - 1);
    }
    if (event.key === "ArrowRight" && index < otpLength - 1) {
      inputRefs.current[index + 1]?.focus();
      setActiveIndex(index + 1);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, otpLength);
    if (!pasted) return;
    const next = buildEmptyDigits(otpLength).map((_, i) => pasted[i] || "");
    setDigits(next);
    clearErrorTimer();
    setError("");
    const focusIndex = Math.min(pasted.length, otpLength - 1);
    inputRefs.current[focusIndex]?.focus();
    setActiveIndex(focusIndex);
  };

  const isResendCooldownActive = resendCooldownSeconds > 0;
  const isResendLockActive = resendLockSeconds > 0 || Boolean(resendLockEndsAt && resendLockEndsAt > Date.now());
  const displayedLockSeconds = resendLockSeconds > 0 ? resendLockSeconds : resendLockEndsAt ? Math.max(0, Math.ceil((resendLockEndsAt - Date.now()) / 1000)) : 0;
  const sendsRemaining = otpMeta?.attemptsRemaining ?? DEFAULT_MAX_OTP_SENDS - 1;
  const canResend = (isEmailTarget || Boolean(mobileNumber)) && sendsRemaining > 0 && !isSending && !isVerifying && !isResendCooldownActive && !isResendLockActive;

  const getResendButtonLabel = () => {
    if (isSending) {
      return "Sending…";
    }
    if (isResendCooldownActive) {
      return `Resend (${resendCooldownSeconds}s)`;
    }
    return `Resend${otpMeta ? ` (${sendsRemaining} left)` : ""}`;
  };

  const verifyOtp = useCallback(
    async (code) => {
      if (code.length !== otpLength || isVerifying) {
        return;
      }

      clearErrorTimer();
      setError("");
      setIsVerifying(true);

      try {
        if (!isEmailTarget) {
          const result = isEditMobileMode
            ? await validateEditMobileOtp({
                emailId: email,
                role: profileApiRole,
                mobileNumber,
                otpCode: code,
              })
            : await validateMobileOtp({
                mobileNumber,
                otpCode: code,
                role: otpApiRole,
              });
          if (!result.success) {
            lastSubmittedOtpRef.current = "";
            setDigits(buildEmptyDigits(MOBILE_OTP_LENGTH));
            setActiveIndex(0);
            inputRefs.current[0]?.focus();
            showOtpError(result.message || "Invalid verification code.");
            return;
          }
          onSuccess?.(result.message || (isEditMobileMode ? "Mobile number updated successfully." : "Mobile number verified successfully."));
          onClose();
          return;
        }

        const result = await validateEmailOtp({
          email,
          otpCode: code,
          purpose: OTP_PURPOSE_EMAIL_VERIFICATION,
          role: otpApiRole,
        });

        if (!result.success) {
          lastSubmittedOtpRef.current = "";
          setDigits(buildEmptyDigits(EMAIL_OTP_LENGTH));
          setActiveIndex(0);
          inputRefs.current[0]?.focus();
          showOtpError(result.message);
          return;
        }

        clearPersistedEmailVerifyResendLock();
        onSuccess?.(result.message || EMAIL_VERIFY_SUCCESS_MESSAGE);
        onClose();
      } catch (error) {
        Sentry.captureException(error);
        showOtpError("Unable to verify code. Please try again.");
      } finally {
        setIsVerifying(false);
      }
    },
    [clearErrorTimer, email, isEditMobileMode, isEmailTarget, isVerifying, mobileNumber, onClose, onSuccess, otpApiRole, otpLength, profileApiRole, showOtpError, target, verificationRole],
  );

  useEffect(() => {
    if (!otpSent || otp.length !== otpLength || isVerifying) {
      return;
    }
    if (lastSubmittedOtpRef.current === otp) {
      return;
    }
    lastSubmittedOtpRef.current = otp;
    verifyOtp(otp);
  }, [otp, otpLength, otpSent, isVerifying, verifyOtp]);

  const handleResend = async () => {
    if (!canResend) return;
    clearErrorTimer();
    setError("");
    setSendError("");
    setIsSending(true);
    try {
      const result = isEmailTarget
        ? await sendEmailOtp({
            email,
            purpose: OTP_PURPOSE_EMAIL_VERIFICATION,
            action: OTP_ACTION_RESEND,
            role: otpApiRole,
          })
        : isEditMobileMode
          ? await sendEditMobileOtp({
              emailId: email,
              role: profileApiRole,
              mobileNumber,
            })
          : await sendMobileOtp({
              mobileNumber,
              role: otpApiRole,
            });

      if (!result.success) {
        if (result.data) {
          if (isEmailTarget) {
            applyOtpMeta(result.data, email, result.message);
          } else {
            setOtpMeta(result.data);
            const cooldownEndsAt = resolveResendCooldownEndsAt(result.data.nextRequestAfter, result.message, result.data.minRequestIntervalSeconds);
            if (cooldownEndsAt) {
              setResendCooldownEndsAt(cooldownEndsAt);
              setResendCooldownSeconds(getCooldownRemainingSeconds(cooldownEndsAt));
            }
          }
        }

        if (isSendOtpRateLimitError(result.data)) {
          if (isEmailTarget) {
            const lockEndsAt = resolveEmailVerifyLockEndsAt({
              email,
              lockedUntil: result.data?.lockedUntil,
              attemptsRemaining: result.data?.attemptsRemaining ?? 0,
            });
            startResendLock(email, result.data);
            if (lockEndsAt) {
              onSendRateLimited?.(lockEndsAt);
            }
            onClose();
          } else {
            const lockEndsAt = parseLockedUntilMs(result.data?.lockedUntil) || (result.data?.attemptsRemaining === 0 ? Date.now() + RESEND_LOCK_MS : null);
            if (lockEndsAt) {
              onSendRateLimited?.(lockEndsAt);
              onClose();
            }
          }
          setSendError("");
          return;
        }

        if (isSendOtpTooSoonError(result.data, result.message) || isOtpWaitMessage(result.message)) {
          if (isEmailTarget && !result.data) {
            startResendCooldown(null, email, result.message);
          }
          setError("");
          return;
        }

        showOtpError(result.message);
        return;
      }

      if (isEmailTarget) {
        applyOtpMeta(result.data, email);
      } else if (result.data) {
        setOtpMeta(result.data);
        const cooldownEndsAt = resolveResendCooldownEndsAt(result.data.nextRequestAfter, "", result.data.minRequestIntervalSeconds);
        if (cooldownEndsAt) {
          setResendCooldownEndsAt(cooldownEndsAt);
          setResendCooldownSeconds(getCooldownRemainingSeconds(cooldownEndsAt));
        }
      }

      lastSubmittedOtpRef.current = "";
      setDigits(buildEmptyDigits(otpLength));
      setActiveIndex(0);
      setResent(true);
      window.setTimeout(() => inputRefs.current[0]?.focus(), 50);
    } catch (error) {
      Sentry.captureException(error);
      showOtpError("Failed to resend verification code.");
    } finally {
      setIsSending(false);
    }
  };

  const resendPrompt = target === "mobile" ? "Didn't receive the SMS?" : "Didn't receive the email?";

  return (
    <Dialog
      className="family-otp-verify-dialog"
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        onClose(event, reason);
      }}
      maxWidth="xs"
      fullWidth
      PaperProps={{ className: "family-otp-verify-paper" }}>
      <DialogContent className="family-modal-content family-otp-verify-content">
        <button type="button" className="family-modal-close" onClick={onClose} aria-label="Close">
          <IoClose />
        </button>

        {otpSent ? (
          <p className="family-otp-verify-intro">
            A verification code has been sent to <strong className="family-otp-verify-contact-strong">{contactDisplay}</strong>. Enter the code below.
          </p>
        ) : isSending && !isResendLockActive ? (
          <p className="family-otp-verify-intro" role="status">
            Sending verification code to <strong className="family-otp-verify-contact-strong">{contactDisplay}</strong>…
          </p>
        ) : null}

        {isResendLockActive && !otpSent ? (
          <p className="family-otp-verify-error" role="alert">
            {formatResendLockError(displayedLockSeconds)}
          </p>
        ) : sendError && !otpSent ? (
          <p className="family-otp-verify-error" role="alert">
            {sendError}
          </p>
        ) : null}

        {otpSent ? (
          <div className="family-otp-verify-form">
            <div className={`family-otp-boxes${otpLength > 4 ? " family-otp-boxes--compact" : ""}`} onPaste={handlePaste}>
              {digits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    inputRefs.current[index] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  autoComplete={index === 0 ? "one-time-code" : "off"}
                  maxLength={1}
                  className={`family-otp-box${activeIndex === index ? " is-active" : ""}${error ? " has-error" : ""}`}
                  value={digit}
                  aria-label={`Digit ${index + 1} of ${otpLength}`}
                  onFocus={() => setActiveIndex(index)}
                  onChange={(e) => updateDigit(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                />
              ))}
            </div>

            {error ? (
              <p className="family-otp-verify-error" role="alert">
                {error}
              </p>
            ) : resent ? (
              <p className="family-otp-verify-resent" role="status">
                A new code has been sent. Please check your {isEmailTarget ? "email" : "messages"} and enter the new code above.
              </p>
            ) : null}

            {isVerifying ? (
              <p className="family-otp-verify-resent" role="status">
                Verifying…
              </p>
            ) : null}
          </div>
        ) : null}

        {otpSent && isEmailTarget && !isResendLockActive ? (
          <p className="family-otp-resend-line">
            {resendPrompt}{" "}
            <button type="button" className="family-otp-resend-link" onClick={handleResend} disabled={!canResend || isSending} aria-live="polite">
              {getResendButtonLabel()}
            </button>
          </p>
        ) : otpSent && !isEmailTarget ? (
          <p className="family-otp-resend-line">
            {resendPrompt}{" "}
            <button type="button" className="family-otp-resend-link" onClick={handleResend} disabled={!canResend || isSending} aria-live="polite">
              {getResendButtonLabel()}
            </button>
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default FamilyOtpVerifyDialog;
